import { Fragment, useEffect } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/useAuth'
import { useMcpSurfaceAccess } from '../lib/mcpAccess'
import { useSectionAccess } from '../lib/roleAccess'
import { getTenantFromHostname, buildTenantStudioUrl } from '../lib/tenant'
import { resolveLegacyHash } from '../lib/legacyHashShim'
import { Login } from '../pages/Login'
import { TopBar } from '../components/TopBar'
import { HomeIcon } from '../components/icons/HomeIcon'
import { AgentIcon } from '../components/icons/AgentIcon'
import { HeartbeatIcon } from '../components/icons/HeartbeatIcon'
import { SettingsIcon } from '../components/icons/SettingsIcon'
import { MarketplaceIcon } from '../components/icons/MarketplaceIcon'
import { WorkflowsIcon } from '../components/icons/WorkflowsIcon'
import { AnalyticsIcon } from '../components/icons/AnalyticsIcon'
import { AuditLogIcon } from '../components/icons/AuditLogIcon'
import { Spinner } from '../components/Spinner'
import { Breadcrumbs } from '../components/Breadcrumbs'

function AuthSplash() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-app)' }} role="status" aria-live="polite">
      <Spinner size={18} />
    </div>
  )
}

/* ─── Nav item ─── */

interface NavItemProps {
  to: string
  icon: React.ReactNode
  label: string
  preview?: boolean
  end?: boolean
}

function NavItem({ to, icon, label, preview = false, end = false }: NavItemProps) {
  const { t } = useTranslation()
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
    >
      <span className="nav-icon">{icon}</span>
      {label}
      {preview && (
        <span className="nav-badge-soon" title={t('common.previewTooltip')}>
          {t('appShell.soon')}
        </span>
      )}
    </NavLink>
  )
}

function NavSectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="nav-section-label">{children}</div>
}

/* ─── App shell ─── */

const REDIRECT_KEY = 'redirect_after_login'
const AUTH_ONLY_PATHS = ['/oauth2/callback', '/access-denied']
const MARKETING_URL = import.meta.env.VITE_MARKETING_URL ?? 'https://mambu.com'

export function AppShell() {
  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated, status, tenantId, baseStudioUrl, didAuthFailed, agentsContracted, entitlementsAvailable, entitlementsPending } = useAuth()
  // MS-866 AC6 — a failed entitlements lookup collapses the nav to Home only,
  // matching the prototype's applyEntitlementsErrorState() (agent-studio.html:6997-7009).
  // Gated on !entitlementsPending too (Decision 3): an already-cached session must
  // render its normal nav on first paint, not flash the degraded state while the
  // mount probe is still resolving.
  const entitlementsDegraded = entitlementsAvailable === false && !entitlementsPending
  /* Both MCP surfaces share one gate so they cannot drift apart — MS-700 AC-1
     ("gated identically to MCP Config"). See lib/mcpAccess.ts. */
  const { canSeeMcpSurfaces } = useMcpSurfaceAccess()
  const { canOpen } = useSectionAccess()
  const { t } = useTranslation()
  const hasTenant = !!getTenantFromHostname()

  useEffect(() => {
    // Migrate legacy theme key on first load; TopBar owns the toggle
    const legacy = localStorage.getItem('spark_theme')
    if (legacy && !localStorage.getItem('studio_theme')) {
      localStorage.setItem('studio_theme', legacy)
      localStorage.removeItem('spark_theme')
    }
    const stored = localStorage.getItem('studio_theme')
    document.documentElement.dataset['theme'] = stored === 'dark' ? 'dark' : 'light'
  }, [])

  // Boot-time compatibility shim for pre-MS-740 fragment links (e.g. `#mcpconfig`
  // bookmarked before routes became addressable paths). See lib/legacyHashShim.ts.
  useEffect(() => {
    if (!window.location.hash) return
    const target = resolveLegacyHash(window.location.hash)
    if (target) navigate(target, { replace: true })
  }, [navigate])

  useEffect(() => {
    if (!isAuthenticated && !AUTH_ONLY_PATHS.includes(location.pathname)) {
      sessionStorage.setItem(REDIRECT_KEY, location.pathname)
    }
  }, [isAuthenticated, location.pathname])

  // Root domain (no tenant subdomain): bounce authenticated users to their tenant,
  // and unauthenticated visitors to the marketing site — never show Login/errors here.
  // A failed authentication attempt is exempt: it must surface the auth-failed Login
  // screen (MS-682 Scenario B) instead of being swallowed by the marketing bounce.
  useEffect(() => {
    if (hasTenant || status === 'checking' || didAuthFailed) return
    if (isAuthenticated && tenantId) {
      window.location.href = buildTenantStudioUrl(tenantId, window.location, baseStudioUrl)
    } else if (!isAuthenticated) {
      window.location.href = MARKETING_URL
    }
  }, [hasTenant, status, isAuthenticated, tenantId, baseStudioUrl, didAuthFailed])

  if (status === 'checking' || (!hasTenant && !didAuthFailed)) {
    return <AuthSplash />
  }

  if (!isAuthenticated) {
    return <Login />
  }

  const isPreviewPage = ['/marketplace', '/workflows', '/agents-analytics'].includes(location.pathname)

  /* Nav is built as data so an empty group's section label can be dropped
     (MS-741 AC3.7) rather than walked imperatively as in the prototype
     (agent-studio.html:6893-6904). Each item is `canOpen(section) && ...` so a
     role-hidden surface disappears from the nav entirely (hide, not preview —
     subscription-gated surfaces stay visible via `preview` instead). */
  const navGroups = [
    {
      label: t('appShell.sections.configure'),
      items: [
        canOpen('agents') && (
          <NavItem key="agents" to="/agents" icon={<AgentIcon />} label={t('appShell.nav.agents')} preview={!agentsContracted} />
        ),
        canOpen('mcp') && canSeeMcpSurfaces && (
          <NavItem key="mcp" to="/mcp" icon={<HeartbeatIcon size={18} />} label={t('appShell.nav.mambuMcp')} />
        ),
        canOpen('global') && (
          <NavItem key="global-settings" to="/global-settings" icon={<SettingsIcon />} label={t('appShell.nav.globalSettings')} />
        ),
      ],
    },
    {
      label: t('appShell.sections.extend'),
      items: [
        canOpen('marketplace') && (
          <NavItem key="marketplace" to="/marketplace" icon={<MarketplaceIcon />} label={t('appShell.nav.marketplace')} preview />
        ),
        canOpen('workflows') && (
          <NavItem key="workflows" to="/workflows" icon={<WorkflowsIcon />} label={t('appShell.nav.workflows')} preview />
        ),
      ],
    },
    {
      label: t('appShell.sections.observe'),
      items: [
        canOpen('audit') && (
          <NavItem key="audit-log" to="/audit-log" icon={<AuditLogIcon />} label={t('appShell.nav.auditLog')} />
        ),
        canOpen('analytics') && (
          <NavItem key="agents-analytics" to="/agents-analytics" icon={<AnalyticsIcon />} label={t('appShell.nav.analytics')} preview />
        ),
        /* MCP Analytics sits after Agents Analytics per the prototype's Observe
           ordering (agent-studio.html:3459-3466) and shares the MCP gate above.
           Same icon as the item above it — both are the bar-chart glyph in the
           prototype; MS-700 AC-4 requires this one to match the overview and page. */
        canOpen('mcp-analytics') && canSeeMcpSurfaces && (
          <NavItem key="mcp-analytics" to="/mcp-analytics" icon={<AnalyticsIcon />} label={t('appShell.nav.mcpAnalytics')} />
        ),
      ],
    },
  ]
    .map(g => ({ ...g, items: g.items.filter(Boolean) }))
    .filter(g => g.items.length > 0)

  return (
    <div className="app-shell">
      <TopBar />

      <div className="shell-body">
        {/* ─── Left nav ─── */}
        <nav aria-label="Main navigation" className="left-nav">
          {/* Nav menu */}
          <div className="left-nav-menu">
            <NavItem to="/home" icon={<HomeIcon />} label={t('appShell.nav.home')} end />

            {!entitlementsDegraded && navGroups.map(group => (
              <Fragment key={group.label}>
                <NavSectionLabel>{group.label}</NavSectionLabel>
                {group.items}
              </Fragment>
            ))}
          </div>
        </nav>

        {/* ─── Main area ─── */}
        <main className="main-area" style={isPreviewPage ? { background: 'radial-gradient(ellipse 60% 50% at 100% 0%, var(--accent-subtle) 0%, var(--bg-app) 55%)' } : undefined}>
          <Breadcrumbs />
          <Outlet />
        </main>
      </div>
    </div>
  )
}
